<template>
  <section class="wood-carving-app">
    <div class="wood-carving-app__padding">
      <div class="wood-carving-app__stage">
        <h2 class="wood-carving-app__title">Carving a Figure Out of Wood</h2>
        <section class="page-area">
          <div class="page page--0" v-if="currentPage === 0">
            <div class="figure--finished"></div>
            <p>
              Carving is a <em>subtractive</em> process. A carver removes bits and pieces of a hard material, slowly revealing the desired shape. Many different things can be carved - stone, marble, wood, plaster, plastic, bone, even soap. A chisel - a sharp blade with a long handle - is used in almost all kinds of carving. A wooden hammer called a mallet is used to tap the chisel from the back.
            </p>
          </div>
          <div class="page page--1" v-if="currentPage === 1">
            <Container group-name="dndPencil" @drop="dndPencilDrop" :should-animate-drop="()=>false" class="interactive-box">
              <Scene1Animation ref="scene1animation"></Scene1Animation>
            </Container>
            <div class="page__instructions">
              <p class="page__title"><strong>Sketch</strong></p>
              <p>Before starting to carve, <strong>drag and release the pencil over the piece of paper</strong> to make a sketch of the sculpture.</p>
              <div class="tool-box">
                <p>Pencil</p>
                <div class="tool-box__area">
                  <Container group-name="dndPencil" :drag-begin-delay="0">
                    <Draggable v-if="!hasToolBeenUsed">
                      <div class="tool tool--pencil"></div>
                    </Draggable>
                  </Container>
                </div>
              </div>
            </div>
          </div>
          <div class="page page--2" v-if="currentPage === 2">
            <Container group-name="dndBlockWood" @drop="dndBlockWoodDrop" :should-animate-drop="()=>false" class="interactive-box">
              <div v-if="scene2.isToolUsedCorrect" class="ui-wood-block"></div>
              <div v-if="scene2.isToolUsedIncorrect">
                <p><strong>Sorry, this is not the right size.</strong></p>
              </div>
            </Container>
            <div class="page__instructions">
              <p class="page__title"><strong>Select Wood</strong></p>
              <p>Select a piece of wood that is the right size and shape for the figure in the sketch. <strong>Drag it to the working area.</strong></p>
              <div class="tool-box">
                <p>Blocks of Wood</p>
                <div class="tool-box__area">
                  <Container group-name="dndBlockWood" :drag-begin-delay="0" :get-child-payload="(index)=> index">
                    <Draggable :class="{'hide-tool': scene2.isToolUsedCorrect}">
                      <div class="tool tool--wood-block-choice-correct"></div>
                    </Draggable>
                    <Draggable :class="{'hide-tool': scene2.isToolUsedIncorrect}">
                      <div class="tool tool--wood-block-choice-incorrect"></div>
                    </Draggable>
                  </Container>
                </div>
              </div>
            </div>
          </div>
          <div class="page page--3" v-show="currentPage === 3">
            {{ currentPage === 3 ? sceneDrawOnTheWood() : ''}}
            <div class="interactive-box">
              <canvas style='position: absolute; top: 0; left: 0;' id="bottomCanvas"></canvas>
              <canvas style='position: absolute; top: 0; left: 0;' id="topCanvas"></canvas>
              <canvas style='display: none;' id="patternCanvas"></canvas>
            </div>
            <div class="page__instructions">
              <p class="page__title"><strong>Draw on the Wood</strong></p>
              <p>A maquette is a small clay model of the sketch in three-dimensional form. <strong>Drag the chalk over the wood</strong> to make a drawing based on the maquette.</p>
              <div class="tool-box">
                <p>Chalk</p>
                <div class="tool-box__area">
                  <vue-draggable-resizable @dragging="onDragging" @dragstop="onDragStop" id="scene3tool">
                    <div class="tool tool--chalk"></div>
                  </vue-draggable-resizable>
                </div>
              </div>
            </div>
          </div>
          <div class="page page--4" v-if="currentPage === 4">
            <Container group-name="dndChiselMallet" @drop="dndChiselMalletDrop" :should-animate-drop="()=>false" class="interactive-box">
              <Scene4Animation ref="scene4animation"></Scene4Animation>
            </Container>
            <div class="page__instructions">
              <p class="page__title"><strong>Rough Out the Form</strong></p>
              <p><strong>Drag and release the mallet and chisel over the wood</strong> to carve a rough outline of the form.</p>
              <div class="tool-box">
                <p>Chisel &amp; <br>Mallet</p>
                <div class="tool-box__area">
                  <Container group-name="dndChiselMallet" :drag-begin-delay="0">
                    <Draggable v-if="!hasToolBeenUsed">
                      <div class="tool tool--combo">
                        <div class="tool tool--chisel"></div>
                        <div class="tool tool--mallet"></div>
                      </div>
                    </Draggable>
                  </Container>
                </div>
              </div>
            </div>
          </div>
          <div class="page page--5" v-if="currentPage === 5">
            <Container group-name="dndRefineCarving" @drop="dndRefineCarvingDrop" :should-animate-drop="()=>false" class="interactive-box">
              <Scene5Animation ref="scene5animation"></Scene5Animation>
            </Container>
            <div class="page__instructions">
              <p class="page__title"><strong>Refine Carving</strong></p>
              <p>Once the main shape has been roughed out we use smaller chisels to carve the smaller features of the figure and make it look more realistic.</p>
              <div class="tool-box">
                <p>Smaller <br> Chisel</p>
                <div class="tool-box__area">
                  <Container group-name="dndRefineCarving" :drag-begin-delay="0">
                    <Draggable v-if="!hasToolBeenUsed">
                      <div class="tool tool--small-chisel"></div>
                    </Draggable>
                  </Container>
                </div>
              </div>
            </div>
          </div>
          <div class="page page--6" v-show="currentPage === 6">
            {{ currentPage === 6 ? sceneFinishingTouches() : ''}}
            <div class="interactive-box">
              <canvas style='position: absolute; top: 0; left: 0;' id="finishTouchBottomCanvas"></canvas>
              <canvas style='position: absolute; top: 0; left: 0;' id="finishTouchTopCanvas"></canvas>
              <canvas style='display: none;' id="finishTouchPatternCanvas"></canvas>
            </div>
            <div class="page__instructions">
              <p class="page__title"><strong>Finishing Touches</strong></p>
              <p>To smooth the wood carving when it is finished, <strong>drag the sandpaper over the figure</strong>. Once the surface of the wood is sealed with wax or paint your carving is complete.</p>
              <div class="tool-box">
                <p>Sandpaper</p>
                <div class="tool-box__area">
                  <vue-draggable-resizable @dragging="onDragging" @dragstop="onDragStop" id="scene6tool">
                    <div class="tool tool--sand-paper"></div>
                  </vue-draggable-resizable>
                </div>
              </div>
            </div>
          </div>

          <div class="wood-carving-app__nav">
            <button @click="prevPage" class="i-btn i-btn--back" v-if="currentPage !== 0 && currentPage !== 6">Back</button>
            <button @click="nextPage" class="i-btn i-btn--next" v-if="currentPage !== 6">Next</button>
            <a class="tool-box" href="https://www.knowitall.org/series/sculpture-studio" v-if="currentPage === 6">Back to Studio</a>
          </div>
        </section>
      </div>
    </div>
  </section>
</template>

<script>
import { Howl } from 'howler'
import VueDraggableResizable from 'vue-draggable-resizable'
import { Container, Draggable } from 'vue-smooth-dnd';
import Scene1Animation from '@/components/scene1animation.vue'
import Scene4Animation from '@/components/scene4animation.vue'
import Scene5Animation from '@/components/scene5animation.vue'

export default {
  name: 'app',
  components: {
    Container,
    Draggable,
    VueDraggableResizable,
    Scene1Animation,
    Scene4Animation,
    Scene5Animation,
  },
  data: function () {
    return {
      isDragging: false,
      hasToolBeenUsed: false,
      parentLock: true,
      currentPage: 0,
      currentSoundId: null,
      audio: new Howl({
        src: ['audio/wood-carving-audio-sprite.mp3'],
        sprite: {
          ding: [43, 740],
          chalk: [823, 586],
          chiselMetal: [1444, 269],
          click: [1678, 100],
          sanding: [1883, 942],
          chiselWood: [2788, 134],
          pencil: [3108, 987],
          buzzer: [4145, 553]
        }
      }),
      scene2: {
        isToolUsedCorrect: false,
        isToolUsedIncorrect: false,
      },
      scene3: {
        isInitalized: false,
      },
      scene6: {
        isInitalized: false,
      }
    }
  },
  methods: {
    nextPage() {
      const pageBeforeLeaving = this.currentPage;
      if (this.currentPage < 6) {
        this.audio.play('click');
        this.currentPage++;
        this.resetTools(pageBeforeLeaving);
      }
    },
    prevPage() {
      const pageBeforeLeaving = this.currentPage;
      if (this.currentPage > 0) {
        this.audio.play('click');
        this.currentPage--;
        this.resetTools(pageBeforeLeaving);
      }
    },
    resetTools(pageNumber) {
      this.hasToolBeenUsed = false;
      if (pageNumber === 2) {
        this.scene2 = {
          isToolUsedCorrect: false,
          isToolUsedIncorrect: false,
        }
      }

      if (pageNumber === 3) {
        this.scene3 = {
          isInitalized: false,
        }
      }

      if (pageNumber === 6) {
        this.scene6 = {
          isInitalized: false,
        }
      }
    },
    dndPencilDrop(dropResult) {
      const { addedIndex } = dropResult;
      if (addedIndex !== null) {
        this.hasToolBeenUsed = true;
        this.animateScene1();
        this.audio.play('pencil');
      }
    },
    dndBlockWoodDrop(dropResult) {
      const { addedIndex, payload } = dropResult;

      if (addedIndex !== null && payload === 0) {
        this.scene2.isToolUsedCorrect = true;
        this.scene2.isToolUsedIncorrect = false;
        this.audio.play('ding');
      }
      if (addedIndex !== null && payload === 1) {
        this.scene2.isToolUsedCorrect = false;
        this.scene2.isToolUsedIncorrect = true;
        this.audio.play('buzzer');
      }
    },
    dndChiselMalletDrop(dropResult) {
      const { addedIndex } = dropResult;
      if (addedIndex !== null) {
        this.hasToolBeenUsed = true;
        this.animateScene4();
      }
    },
    dndRefineCarvingDrop(dropResult) {
      const { addedIndex } = dropResult;
      if (addedIndex !== null) {
        this.hasToolBeenUsed = true;
        this.animateScene5();
      }
    },
    onDragging() {
      if (!this.isDragging) {
        this.isDragging = true;
      }

      if (this.currentPage === 3) {
        if (!this.audio.playing(this.currentSoundId)) {
          this.currentSoundId = this.audio.play('chalk');
        }
      }

      if (this.currentPage === 6) {
        if (!this.audio.playing(this.currentSoundId)) {
          this.currentSoundId = this.audio.play('sanding');
        }
      }
    },
    onDragStop() {
      if (this.isDragging) {
        this.isDragging = false;
        this.currentSoundId = null;
      }
    },
    drawPattern(x, y, topCtx, bottomCtx, patternCanvas) {
      if (this.isDragging) {
        topCtx.fillStyle = bottomCtx.createPattern(patternCanvas, 'no-repeat');
        // topCtx.fillStyle = '#F00000';
        topCtx.beginPath();
        topCtx.arc(x, y, ((10 * window.innerWidth)/725), 0, Math.PI * 2, true);
        topCtx.fill();
      }
    },
    sceneDrawOnTheWood() {
      if (this.scene3.isInitalized) {
        return;
      }

      this.scene3.isInitalized = true;

      let bottomCanvas = document.getElementById('bottomCanvas');
      let topCanvas = document.getElementById('topCanvas');
      let patternCanvas = document.getElementById('patternCanvas');

      const bottomCtx = bottomCanvas.getContext('2d');
      const patternCtx = patternCanvas.getContext('2d');

      const bottomImage = new Image();
      const topImage = new Image();

      bottomImage.onload = function () {
        bottomCanvas.width = bottomCanvas.clientWidth;
        bottomCanvas.height = bottomCanvas.clientHeight;
        bottomCtx.drawImage(bottomImage, 0, 0, bottomImage.width, bottomImage.height, 0, 0, bottomCanvas.clientWidth, bottomCanvas.clientHeight);
      };

      topImage.onload = function() {
        patternCanvas.width = bottomCanvas.clientWidth;
        patternCanvas.height = bottomCanvas.clientHeight;
        patternCtx.drawImage(topImage, 0, 0, topImage.width, topImage.height, 0, 0, bottomCanvas.clientWidth, bottomCanvas.clientHeight);
      };

      bottomImage.src = require('./assets/img/wood-block--unchalked.svg');
      topImage.src = require('./assets/img/wood-block--chalked.svg');

      const tool = document.getElementById('scene3tool');
      const woodDrawingSettings = {
        tool: tool,
        topCanvas: topCanvas,
        bottomCanvas: bottomCanvas,
        patternCanvas: patternCanvas,
        onMouseMove: this.drawPattern,
        preventDefault: true
      };

      this.setupMouse(woodDrawingSettings);
    },
    sceneFinishingTouches() {
      if (this.scene6.isInitalized) {
        return;
      }

      this.scene6.isInitalized = true;

      let bottomCanvas = document.getElementById('finishTouchBottomCanvas');
      let topCanvas = document.getElementById('finishTouchTopCanvas');
      let patternCanvas = document.getElementById('finishTouchPatternCanvas');

      const bottomCtx = bottomCanvas.getContext('2d');
      const patternCtx = patternCanvas.getContext('2d');

      const bottomImage = new Image();
      const topImage = new Image();

      bottomImage.onload = function () {
        bottomCanvas.width = bottomCanvas.clientWidth;
        bottomCanvas.height = bottomCanvas.clientHeight;
        bottomCtx.drawImage(bottomImage, 0, 0, bottomImage.width, bottomImage.height, 0, 0, bottomCanvas.clientWidth, bottomCanvas.clientHeight);
      };

      topImage.onload = function() {
        patternCanvas.width = bottomCanvas.clientWidth;
        patternCanvas.height = bottomCanvas.clientHeight;
        patternCtx.drawImage(topImage, 0, 0, topImage.width, topImage.height, 0, 0, bottomCanvas.clientWidth, bottomCanvas.clientHeight);
      };

      bottomImage.src = require('./assets/img/wood-block--refine--step2.svg');
      topImage.src = require('./assets/img/wood-block--finished.svg');

      const tool = document.getElementById('scene6tool');
      const finishingTouchesSettings = {
        tool: tool,
        topCanvas: topCanvas,
        bottomCanvas: bottomCanvas,
        patternCanvas: patternCanvas,
        onMouseMove: this.drawPattern,
        preventDefault: true
      };

      this.setupMouse(finishingTouchesSettings);
    },
    setupMouse(settings) {
      let {
        topCanvas,
        bottomCanvas,
        patternCanvas,
        onMouseMove,
        preventDefault,
        tool,
      } = settings;

      let mouse = {
        x: 0,
        y: 0,
        down: false
      };

      let rectLeft, rectTop;
      const bottomCtx = bottomCanvas.getContext('2d'); 
      const topCtx = topCanvas.getContext('2d'); 
      const hook = topCanvas.addEventListener.bind(topCanvas);
      const mouseDown = updateMouseStatus.bind(null, true);
      const mouseUp = updateMouseStatus.bind(null, false);
      const toolHook = tool.addEventListener.bind(tool);

      toolHook('touchmove', updateCoordinates);
      hook('mousedown', mouseDown);
      hook('mouseup', mouseUp);
      hook('mousemove', updateCoordinates);
      hook('scroll', updateRect);

      function updateMouseStatus(b, e) {
        mouse.down = b;
        updateCoordinates(e);
        if (preventDefault) {
          e.stopPropagation();
          e.preventDefault();
        }
      }

      function updateCoordinates(e) {
        if (e.type === 'touchmove') {
          mouse.x = (Math.floor(e.touches[0].clientX) - rectLeft);
          mouse.y = (Math.floor(e.touches[0].clientY) - rectTop);
        } else {
          mouse.x = (e.clientX - rectLeft);
          mouse.y = (e.clientY - rectTop);
        }

        onMouseMove(mouse.x, mouse.y, topCtx, bottomCtx, patternCanvas);
        updateRect();
      }

      function updateRect() {
        const cWidth = topCanvas.clientWidth;
        const cHeight = topCanvas.clientHeight;

        if (topCanvas.width !== cWidth || topCanvas.height !== cHeight) {
          topCanvas.width = cWidth;
          topCanvas.height = cHeight;
        }

        const rect = topCanvas.getBoundingClientRect();
        rectLeft = Math.floor(rect.left);
        rectTop = Math.floor(rect.top);
      }

      updateRect();
    },
    animateScene1() {
      this.$refs.scene1animation.animate();
      setTimeout(() => {
        this.audio.play('pencil');
      }, 1000);
      setTimeout(() => {
        this.audio.play('pencil');
      }, 2000);
      setTimeout(() => {
        this.audio.play('pencil');
      }, 3000);
      setTimeout(() => {
        this.audio.play('pencil');
      }, 4000);
    },
    animateScene4() {
      this.$refs.scene4animation.animate();

      setTimeout(() => {
        this.audio.play('chiselWood');
      }, 1000);
      setTimeout(() => {
        this.audio.play('chiselWood');
      }, 1500);
      setTimeout(() => {
        this.audio.play('chiselWood');
      }, 3000);
    },
    animateScene5() {
      this.$refs.scene5animation.animate();

      setTimeout(() => {
        this.audio.play('chiselMetal');
      }, 1000);
      setTimeout(() => {
        this.audio.play('chiselMetal');
      }, 2000);
      setTimeout(() => {
        this.audio.play('chiselMetal');
      }, 3000);
    }
  }
}
</script>

<style lang="scss">
@import "style";

.hide-tool {
  z-index: -1;
}

.smooth-dnd-container {
  .animated {
    transform: none !important;
  }
}

.wood-carving-app {
  background: $yellow;
  margin: 0;
  padding: 0;

  &__nav {
    position: absolute;
    bottom: fluid(-16px);
    right: fluid(112px);
    width: fluid(160px);
    height: fluid(52px);

    button {
      font-family: $cursive;
      text-transform: uppercase;
      cursor: pointer;
    }
  }

  &__padding {
    position: relative;
    padding: 0 0 #{(380/725)*100%};
  }

  &__stage {
    padding: fluid(16px);
    overflow: hidden;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height:100%;
  }

  &__title {
    font-family: $cursive;
    font-weight: 400;
    color: #605D22;
    text-shadow: -1px 0 0 #000;
    text-align: center;
    margin: 0 auto;
    font-size: fluid(36px);
  }
}

.page-area {
  margin-top: 1rem;
  position: relative;
}

.page {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  max-width: fluid(550px);
  max-height: fluid(251px);
  margin: 0 auto;

  &__title {
    text-transform: uppercase;
    margin: 0;
  }

  &__instructions {
    flex: 0 0 auto;
    width: fluid(230px);
  }

  p {
    font-size: fluid(14px);
  }

  &--0 { // first/intro page
    justify-content: center;
    .figure--finished {
      flex: 0 0 auto;
      background: url('~@/assets/img/figure--finished.svg');
      background-size: contain;
      background-repeat: no-repeat;
      width: fluid(260px);
      height: fluid(300px);
    }

    p {
      margin-top: fluid(32px);
      margin-left: fluid(16px);
    }
  }

  &--2 {
    .tool-box {
      padding-bottom: 0;
      flex-flow: row wrap;
      justify-content: center;
    }

    .tool-box__area {
      position: relative;
      top: auto;
      left: auto;
      flex: 0 0 100%;

      .smooth-dnd-container {
        display: flex;
        justify-content: center;
      }
    }
  }
}

.ui-paper {
  flex: 0 0 auto;
  background: #fff;
  width: fluid(256px);
  height: fluid(251px);
  box-shadow: fluid(-4px) fluid(4px) 0 #000;
}

.ui-wood-block {
  background-image: url('~@/assets/img/wood-block.svg');
  background-size: contain;
  background-repeat: no-repeat;
  width: fluid(220px);
  height: fluid(239px);
}

.tool-box {
  background: #9E9B5C;
  padding: fluid(16px);
  border-radius: fluid(14px);
  display: flex;
  position: relative;
  box-shadow: fluid(-3px) fluid(3px) 0px 0px rgba(0,0,0,0.5);
  color: #fff;
  text-decoration: none;
  font-family: $cursive;

  p {
    font-family: $cursive;
    text-transform: uppercase;
    color: #fff;
    font-size: fluid(20px);
    margin: 0;
  }

  &__area {
    position: absolute;
    top: 5%;
    left: 50%;
  }
}

a.tool-box {
  padding: 0;
  justify-content: center;

  &:hover {
    text-decoration: underline;
  }
}

.tool {
  background-repeat: no-repeat;
  background-size: contain;
  cursor: pointer;

  &--combo {
    display: flex;
  }

  &--pencil {
    background-image: url('~@/assets/img/pencil.svg');
    width: fluid(56px);
    height: fluid(56px);
  }

  &--wood-block-choice-correct {
    background-image: url('~@/assets/img/wood-block-choice--correct.svg');
    width: fluid(56px);
    height: fluid(56px);
  }

  &--wood-block-choice-incorrect {
    background-image: url('~@/assets/img/wood-block-choice--incorrect.svg');
    width: fluid(102px);
    height: fluid(56px);
  }

  &--chalk {
    background-image: url('~@/assets/img/chalk.svg');
    width: fluid(30px);
    height: fluid(50px);
  }

  &--sand-paper {
    background-image: url('~@/assets/img/sand-paper.svg');
    width: fluid(76px);
    height: fluid(57px);
  }

  &--chisel {
    background-image: url('~@/assets/img/chisel.svg');
    width: fluid(64px);
    height: fluid(69px);
  }

  &--mallet {
    background-image: url('~@/assets/img/mallet.svg');
    width: fluid(69px);
    height: fluid(73px);
  }

  &--small-chisel {
    background-image: url('~@/assets/img/small-chisel.svg');
    width: fluid(45px);
    height: fluid(67px);
  }
}

.draggable {
  width: auto !important;
  height: auto !important;
}

.interactive-box {
  position: relative;
  // border: 1px solid red;
  width: fluid(256px);
  height: fluid(251px);

  canvas {
    width: 100%;
    height: 100%;
  }
}

.i-btn {
  appearance: none;
  border: 0;
  background: transparent;
  margin: 0;

  &:before {
    content: '';
    width: fluid(30px);
    height: fluid(30px);
    background-position: center center;
    background-repeat: no-repeat;
    background-size: contain;
    display: block;
    margin: 0 auto;
  }

  &--back {
    float: left;
    &:before {
      background-image: url('~@/assets/img/button_arrow_l.svg');
    }
  }

  &--next {
    float: right;
    &:before {
      background-image: url('~@/assets/img/button_arrow_r.svg');
    }
  }
}

</style>
